import { useQuery } from '@tanstack/react-query';
import { getEigenWeken, getMedewerkerWeken, type EigenWekenoverzicht } from '../work-quality.api';
import type { TrendLezing } from '../trend-context';

/** Wiens overzicht: het eigen (persoon uit de sessie) of dat van één collega. */
export type TrendBron = { soort: 'eigen' } | { soort: 'collega'; userId: number | null };

const isObject = (waarde: unknown): waarde is Record<string, unknown> => typeof waarde === 'object' && waarde !== null;

/** Een werksoort is een object; draagt hij `velden`, dan is dat een lijst van objecten met een veldpad. */
function isWerksoort(waarde: unknown): boolean {
  if (!isObject(waarde)) return false;
  if (waarde.velden === undefined) return true;
  return Array.isArray(waarde.velden) && waarde.velden.every((v) => isObject(v) && typeof v.veld === 'string');
}

/** Een week met een reden, of een gemeten week met beide werksoorten. */
function isWeek(waarde: unknown): boolean {
  if (!isObject(waarde) || typeof waarde.weekKey !== 'string') return false;
  if (waarde.reden !== undefined) return true;
  const werksoorten = waarde.werksoorten;
  return isObject(werksoorten) && isWerksoort(werksoorten.myxxtract) && isWerksoort(werksoorten.pca);
}

/**
 * Alles wat de trend en de tab Maand lezen wordt gecontroleerd, tot op het veld: een weekantwoord, een
 * `null` waar een werksoort of veld hoort, of een ontbrekende drempel is een storing en geen overzicht dat
 * halve cijfers toont.
 */
function isWekenoverzicht(waarde: unknown): waarde is EigenWekenoverzicht {
  if (!isObject(waarde)) return false;
  return (
    typeof waarde.userId === 'number' &&
    typeof waarde.drempelProductenPerWerksoort === 'number' &&
    typeof waarde.drempelProductenPerVeld === 'number' &&
    Array.isArray(waarde.weken) &&
    waarde.weken.every(isWeek)
  );
}

/**
 * Het overzicht over weken van de eerste tot en met de laatste van `weken` (oudste eerst), in één verzoek
 * (SPEC-meerweekenoverzicht-feedback CAP-6 en CAP-7). Het enige ophaalpad voor de trend en de tab Maand.
 *
 * Een storing, een antwoord dat geen overzicht is, of het overzicht van een andere persoon dan de gevraagde
 * collega is `fout`, nooit een leeg overzicht (AD-27). `actief: false` vraagt niets op.
 */
export function useWekenoverzicht(bron: TrendBron, weken: readonly string[], actief = true): TrendLezing {
  const van = weken[0] ?? '';
  const tot = weken[weken.length - 1] ?? '';
  const collega = bron.soort === 'collega' ? bron.userId : null;

  const query = useQuery({
    queryKey: ['workQuality', 'weeks', bron.soort, collega, van, tot],
    queryFn: async () => {
      // Een collega vraagt nooit het eigen overzicht, ook niet zonder nummer (dan staat de query uit).
      const overzicht: unknown = bron.soort === 'eigen' ? await getEigenWeken(van, tot) : await getMedewerkerWeken(collega!, van, tot);
      if (!isWekenoverzicht(overzicht) || (bron.soort === 'collega' && overzicht.userId !== collega)) {
        throw new Error('Het antwoord is geen overzicht over weken van de gevraagde persoon');
      }
      return overzicht;
    },
    enabled: actief && weken.length > 0 && (bron.soort === 'eigen' || collega !== null),
    refetchOnWindowFocus: false,
  });

  if (query.isError) return { status: 'fout' };
  if (query.data !== undefined) return { status: 'klaar', overzicht: query.data, weken };
  return { status: 'laden' };
}
